<script setup lang="ts">
import PersonalityTraitsBarGraph from "@/pages/Insights/ReportsDetailPage/components/PersonalityTraitsBarGraph.vue";
import {ref} from "vue";

const overviewType = ref("boxes");

const props = defineProps(
  {
    reportData: {
      type: Object,
      required: true
    },
    showBreakdown: {
      type: Boolean,
      default: true
    },
    loading: {
      type: Boolean,
      default: false
    }
  }
);

const content = {
  "open_mindedness": {
    label: "Openness",
    icon: "fa fa-feather",
    color: "#ffe6dc"
  },
  "conscientiousness": {
    label: "Conscientiousness",
    icon: "fa fa-tasks",
    color: "#ffebcc"
  },
  "extraversion": {
    label: "Extraversion",
    icon: "fa fa-users",
    color: "#e6f0ff"
  },
  "agreeableness": {
    label: "Agreeableness",
    icon: "fa fa-handshake",
    color: "#e6ffeb"
  },
  "negative_emotionality": {
    label: "Neuroticism",
    icon: "fa fa-heartbeat",
    color: "#ffe6e0"
  }
};

const labels = {
  "extremely_high": "Extremely High",
  "very_high": "Very High",
  "high": "High",
  "medium": "Medium",
  "low": "Low",
  "very_low": "Very Low"
};

function calculateRatio(value: number) {
  if (value === 0) return 0;
  return Math.round((value / props.reportData.total_count[0].count) * 100 * 100) / 100;
}
</script>

<template>
  <v-row v-if="loading">
    <v-col cols="12">
      <v-card variant="flat" class="metric-tile">
        <v-card-text>
          <v-row align-content="center">
            <v-col cols="auto">
              <v-skeleton-loader type="avatar" width="24" height="24"/>
            </v-col>
            <v-col cols="auto">
              <v-skeleton-loader type="heading" width="300"/>
            </v-col>
            <v-spacer/>
            <v-col cols="auto">
              <v-skeleton-loader type="button" width="60"/>
              <v-skeleton-loader type="button" width="60" class="ml-2"/>
            </v-col>
          </v-row>

          <div class="d-flex flex-wrap justify-space-between pa-4">
            <div
              v-for="n in 5"
              :key="n"
              class="d-flex flex-grow-1 px-2"
              style="flex: 1 1 0; max-width: 20%;">
              <v-card class="metric-tile" width="100%" variant="flat">
                <v-card-text>
                  <v-skeleton-loader type="avatar" width="40" height="40" class="mb-2"/>
                  <v-skeleton-loader type="heading" width="80" class="mb-4"/>

                  <div v-for="i in 5" :key="i" class="mt-2">
                    <div class="pb-1 d-flex justify-space-between">
                      <v-skeleton-loader type="text" width="60"/>
                      <v-skeleton-loader type="text" width="30"/>
                    </div>
                    <v-skeleton-loader type="divider" height="4"/>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>

  <v-row v-else>
    <v-col cols="12">
      <v-card variant="flat" class="metric-tile">
        <v-card-text>
          <v-row align-content="center">
            <v-col cols="auto">
              <v-icon>far fa-pie-chart</v-icon>
            </v-col>
            <v-col cols="auto">
              <h2>Big Five Personality Traits Overview</h2>
            </v-col>
            <v-spacer/>
            <v-col cols="auto">
              <v-btn :variant="overviewType === 'boxes' ? 'outlined' : 'text'" @click="overviewType = 'boxes'">
                Boxes
              </v-btn>
              <v-btn :variant="overviewType === 'charts' ? 'outlined' : 'text'" @click="overviewType = 'charts'">
                Charts
              </v-btn>
            </v-col>
          </v-row>

          <div v-if="overviewType === 'boxes'" class="d-flex flex-wrap justify-space-between pa-4">
            <div
              v-for="item in ['open_mindedness', 'conscientiousness', 'extraversion', 'agreeableness', 'negative_emotionality']"
              :key="item"
              class="d-flex flex-grow-1 px-2"
              style="flex: 1 1 0; max-width: 20%;">
              <v-card class="metric-tile" width="100%" variant="flat"
                      :color="content[item as keyof typeof content].color">
                <v-card-text>
                  <div class="pa-4 rounded-lg bg-white d-inline-block">
                    <v-icon>{{ content[item as keyof typeof content].icon }}</v-icon>
                  </div>
                  <h3>{{ content[item as keyof typeof content].label }}</h3>

                  <v-row v-for="ranking in Object.keys(labels)" :key="`${item}_${ranking}`" class="mt-2" dense
                         no-gutters>
                    <v-col cols="12" dense>
                      <div class="pb-1 d-flex justify-space-between text-caption">
                        <span>{{ labels[ranking as keyof typeof labels] }}</span>
                        <span
                          v-if="reportData[item as keyof typeof reportData] &&
                            reportData[item as keyof typeof reportData][0] &&
                            reportData[item as keyof typeof reportData][0][ranking]">
                          {{ calculateRatio(reportData[item as keyof typeof reportData][0][ranking]) }}%
                        </span>
                        <span v-else>0%</span>
                      </div>
                      <v-progress-linear
                        v-if="reportData[item as keyof typeof reportData] &&
                          reportData[item as keyof typeof reportData][0] &&
                          reportData[item as keyof typeof reportData][0][ranking]"
                        :model-value="calculateRatio(reportData[item as keyof typeof reportData][0][ranking])"
                        rounded
                        height="4"/>
                      <v-progress-linear v-else :model-value="0" rounded height="4"/>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </div>
          </div>
          <v-row v-else>
            <v-col
              v-for="item in ['open_mindedness', 'conscientiousness', 'extraversion', 'agreeableness', 'negative_emotionality']"
              :key="item"
              cols="6"
            >
              <v-card class="metric-tile" width="100%" variant="flat"
                      :color="content[item as keyof typeof content].color">
                <v-card-text>
                  <div class="pa-4 rounded-lg bg-white d-inline-block">
                    <v-icon>{{ content[item as keyof typeof content].icon }}</v-icon>
                  </div>
                  <h3>{{ content[item as keyof typeof content].label }}</h3>
                  <personality-traits-bar-graph
                    v-if="reportData[item as keyof typeof reportData] && reportData[item as keyof typeof reportData][0]"
                    :label="content[item as keyof typeof content].label"
                    :report-data="reportData[item as keyof typeof reportData][0]"/>
                  <div v-else class="text-center grey--text pa-4">
                    No data available for this trait.
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<style scoped>

</style>
